'use client';

import Script from 'next/script';
import { useEffect, useState } from 'react';

declare global {
  interface Window {
    FS: any;
    _fs_host: string;
    _fs_script: string;
    _fs_org: string;
    _fs_namespace: string;
  }
}

// Track if FullStory is ready
let isFullStoryReady = false;

// Function to check if FullStory is ready
export const isFullStoryLoaded = () => {
  return typeof window !== 'undefined' && window.FS && isFullStoryReady;
};

// Function to identify a user in FullStory
export const identifyUser = (userId: string, userType: 'user' | 'coach', additionalProps?: Record<string, any>) => {
  if (isFullStoryLoaded()) {
    console.log('Identifying user in FullStory', userId, userType, additionalProps);
    window.FS.identify(userId, {
      userType,
      appName: 'Reframe Meetings',
      ...additionalProps
    });
    return true;
  }
  return false;
};

// Function to clear user identification
export const clearUserIdentification = () => {
  if (isFullStoryLoaded()) {
    window.FS.anonymize();
  }
};

export default function FullStory() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Check if FullStory is already loaded
    if (typeof window !== 'undefined' && window.FS) {
      isFullStoryReady = true;
      setIsLoaded(true);
    }
  }, []);

  return (
    <Script
      id="fullstory"
      strategy="afterInteractive"
      onLoad={() => {
        console.log('FullStory script loaded');
        isFullStoryReady = true;
        setIsLoaded(true);
      }}
      onError={(e) => {
        console.error('Error loading FullStory:', e);
      }}
      dangerouslySetInnerHTML={{
        __html: `
          window['_fs_host'] = 'fullstory.com';
          window['_fs_script'] = 'edge.fullstory.com/s/fs.js';
          window['_fs_org'] = '15S05Z';
          window['_fs_namespace'] = 'FS';
          !function(m,n,e,t,l,o,g,y){var s,f,a=function(h){
          return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e)
          ;function p(b){var h,d=[];function j(){h&&(d.forEach((function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}
          d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)})),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{
          then:function(b,h){return p((function(r,i){d.push([b,h,r,i]),j()}))}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){
          h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)}
          ;function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
          "function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
          (o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onerror=function(){y("Error loading "+l)}
          ;var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}
          function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({
          uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{
          name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},
          g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",
          f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")
          }(window,document,window._fs_namespace,"script",window._fs_script);
        `,
      }}
    />
  );
} 